import React, { useState } from "react";
import styled from "styled-components";
import { Button, Alert } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { colors } from "utils";
import { trackMeta } from "components/pages/Educator/Dashboard/AssignmentGroupCard";

// Shared presentational "Edit Group Focus" picker chrome + section/row primitives.
// EditGroupFocus (Differentiation) and LessonEditGroupFocus both render this; each
// page owns its own data fetching, section content, selection state, and save.

const PageContainer = styled.div`
  background-color: ${colors.milkGlass200};
  display: flex;
  flex-direction: column;
  height: 100vh;
`;

const ContentHeader = styled.div`
  align-items: center;
  background-color: ${colors.nightlyWoods500};
  display: flex;
  gap: 24px;
  padding: 24px 32px;
`;

const HeaderTitle = styled.h1`
  color: ${colors.milkGlass500};
  font-size: 40px;
  font-weight: 700;
  line-height: 44px;
  margin: 0;
`;

const CurrentBar = styled.div`
  align-items: center;
  background-color: white;
  border-bottom: 1px solid ${colors.milkGlass500};
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 16px 32px;
`;

const CurrentLabel = styled.span`
  color: ${colors.nightlyWoods500};
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
`;

export const Tag = styled.span`
  background-color: ${(p) => p.$bg};
  border: 1px solid ${(p) => p.$border};
  border-radius: 11px;
  color: ${(p) => p.$color};
  font-size: 14px;
  line-height: 20px;
  padding: 2px 8px;
  white-space: nowrap;
`;

const CurrentCode = styled.span`
  color: ${colors.nightlyWoods500};
  font-family: "ABC Marfa", sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.5px;
  line-height: 20px;
  text-transform: uppercase;
`;

const CurrentName = styled.span`
  color: ${colors.nightlyWoods500};
  font-size: 14px;
  line-height: 20px;
`;

const MainScroll = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
  padding: 8px 32px 96px;
`;

const DIVIDER = "#ACAFAD";

const SectionHeaderBtn = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  border-bottom: 1px solid ${DIVIDER};
  cursor: ${(p) => (p.$disabled ? "default" : "pointer")};
  display: flex;
  gap: 16px;
  opacity: ${(p) => (p.$disabled ? 0.5 : 1)};
  padding: 16px 8px;
  padding-left: ${(p) => 8 + p.$level * 24}px;
  text-align: left;
  width: 100%;
`;

const SectionTitle = styled.span`
  color: ${colors.nightlyWoods500};
  font-family: "ABC Marfa", sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 26px;
`;

const Chevron = styled.svg`
  flex-shrink: 0;
  transform: rotate(${(p) => (p.$expanded ? "180deg" : "0deg")});
  transition: transform 0.2s ease;
`;

const Collapsible = styled.div`
  display: grid;
  grid-template-rows: ${(p) => (p.$expanded ? "1fr" : "0fr")};
  transition: grid-template-rows 0.25s ease;
`;

const CollapsibleInner = styled.div`
  min-height: 0;
  overflow: hidden;
`;

const Row = styled.label`
  align-items: flex-start;
  border-bottom: 1px solid ${DIVIDER};
  cursor: ${(p) => (p.$disabled ? "not-allowed" : "pointer")};
  display: flex;
  gap: 16px;
  padding: 8px 16px 8px ${(p) => p.$pl}px;
`;

// A standard with no questions can't back a group code. The body dims, but the
// reason is also spelled out below — opacity alone carries nothing to a screen
// reader and reads as a contrast regression.
const DimmedBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  opacity: ${(p) => (p.$disabled ? 0.5 : 1)};
`;

const NoQuestionsNote = styled.span`
  color: ${colors.nightlyWoods500};
  font-size: 14px;
  font-style: italic;
  line-height: 20px;
`;

const Radio = styled.input`
  appearance: none;
  -webkit-appearance: none;
  background-color: white;
  border: 2px solid ${colors.nightlyWoods500};
  border-radius: 50%;
  cursor: pointer;
  display: grid;
  flex-shrink: 0;
  height: 24px;
  margin: 0;
  place-content: center;
  width: 24px;
  &::before {
    background-color: ${colors.green700};
    border-radius: 50%;
    content: "";
    height: 12px;
    transform: scale(0);
    transition: transform 0.1s ease;
    width: 12px;
  }
  &:checked {
    border-color: ${colors.green700};
  }
  &:checked::before {
    transform: scale(1);
  }
  &:focus-visible {
    outline: 2px solid ${colors.green700};
    outline-offset: 2px;
  }
`;

const RowBody = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const TagRow = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

const RowCode = styled.span`
  color: #f7622b;
  font-family: "ABC Marfa", sans-serif;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  line-height: 18px;
  text-transform: uppercase;
`;

const RowName = styled.span`
  color: ${colors.nightlyWoods500};
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
`;

const RowDescription = styled.span`
  color: ${colors.nightlyWoods500};
  font-size: 16px;
  line-height: 22px;
`;

const ViewQuestionsLink = styled.button`
  align-items: center;
  align-self: flex-start;
  background: transparent;
  border: none;
  color: ${colors.nightlyWoods500};
  cursor: pointer;
  display: inline-flex;
  font-size: 14px;
  font-weight: 700;
  gap: 8px;
  line-height: 20px;
  padding: 4px 0;
`;

const Triangle = styled.span`
  display: inline-flex;
  transform: rotate(${(p) => (p.$expanded ? "90deg" : "0deg")});
  transition: transform 0.15s ease;
`;

const ExpandedRegion = styled.div`
  border-bottom: 1px solid ${DIVIDER};
  padding-right: 16px;
  padding-bottom: 16px;
`;

const Footer = styled.div`
  background-color: #fffefe;
  border-top: 1px solid ${colors.blue100};
  box-shadow: 0 -1px 1.5px rgba(0, 0, 0, 0.05);
  display: flex;
  gap: 24px;
  justify-content: flex-end;
  padding: 16px 32px;
`;

const ActionButton = styled(Button)`
  border-radius: 8px;
  font-family: "Inter", sans-serif;
  font-size: 18px;
  font-weight: 700;
  height: 48px;
  letter-spacing: 0.25px;
  line-height: 26px;
  padding: 0 16px;
  width: 180px;
`;

const CancelButton = styled(ActionButton)`
  background: white;
  border: 1px solid #605dec;
  color: #605dec;
  &:hover, &:focus, &:active {
    background: #f4f4fd !important;
    border-color: #605dec !important;
    color: #605dec !important;
  }
`;

const AssignButton = styled(ActionButton)`
  background-color: #605dec;
  border: none;
  color: #faf7f2;
  &:hover, &:focus, &:active {
    background-color: #4f4dd4 !important;
    border: none !important;
  }
  &:disabled {
    background-color: #d5d3ce;
    color: #faf7f2;
  }
`;

const Centered = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  justify-content: center;
`;

const HeaderFill = styled.div`
  flex: 1;
  min-width: 0;
`;

const ChevronIcon = ({ expanded }) => (
  <Chevron $expanded={expanded} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={colors.nightlyWoods500} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="6 9 12 15 18 9" />
  </Chevron>
);

const TriangleIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
    <path d="M2.5 1.5 L8 5 L2.5 8.5 Z" fill={colors.nightlyWoods500} />
  </svg>
);

export const AccordionSection = ({ title, header, right, level = 0, defaultExpanded = false, disabled = false, children }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  return (
    <div>
      <SectionHeaderBtn
        type="button"
        $level={level}
        $disabled={disabled}
        onClick={() => !disabled && setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        <ChevronIcon expanded={expanded} />
        {header ? <HeaderFill>{header}</HeaderFill> : <SectionTitle style={{ flex: 1 }}>{title}</SectionTitle>}
        {right}
      </SectionHeaderBtn>
      <Collapsible $expanded={expanded && !disabled}>
        <CollapsibleInner>{children}</CollapsibleInner>
      </Collapsible>
    </div>
  );
};

export const StandardRadioRow = ({
  option,
  checked,
  pl,
  onSelect,
  onViewQuestions,
  // Inline mode: when `onToggleView` is provided, "View Questions" toggles an inline
  // region (rendered below the row, OUTSIDE the label so its controls don't toggle the
  // radio) instead of opening a modal.
  expanded = false,
  onToggleView,
  expandedContent = null,
  // No question-bearing content behind this standard — selecting it would create a
  // group code with nothing to answer.
  disabled = false,
}) => {
  const meta = option.trackType === "core" ? trackMeta("core") : null;
  // A core option can address a set of standards (lesson flow); render them joined.
  // Everything else passes a single `code`.
  const codeLabel = option.codes?.length ? option.codes.join(", ") : option.code;
  const inline = Boolean(onToggleView);
  const onViewClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (inline) onToggleView(option);
    else onViewQuestions(option);
  };
  return (
    <>
      <Row $pl={pl} $disabled={disabled}>
        <Radio
          type="radio"
          name="edit-group-focus"
          checked={checked}
          disabled={disabled}
          aria-describedby={disabled ? `${option.code}-no-questions` : undefined}
          // The whole row is a <label>, so guard the handler too rather than lean
          // on the browser suppressing activation of a disabled input.
          onChange={() => !disabled && onSelect(option)}
        />
        <RowBody>
          <DimmedBody $disabled={disabled}>
            <TagRow>
              {meta && <Tag $bg={meta.tagBg} $border={meta.tagBorder} $color={meta.tagText}>{meta.label}</Tag>}
              <RowCode>STANDARD - {codeLabel}</RowCode>
            </TagRow>
            <RowName>{option.name}</RowName>
            {option.description && option.description !== option.name && (
              <RowDescription>{option.description}</RowDescription>
            )}
          </DimmedBody>
          {disabled && (
            <NoQuestionsNote id={`${option.code}-no-questions`}>
              No questions available for this standard
            </NoQuestionsNote>
          )}
          <ViewQuestionsLink type="button" aria-expanded={inline ? expanded : undefined} onClick={onViewClick}>
            <Triangle $expanded={inline && expanded}>
              <TriangleIcon />
            </Triangle>
            View Questions
          </ViewQuestionsLink>
        </RowBody>
      </Row>
      {inline && expanded && expandedContent && (
        <ExpandedRegion style={{ paddingLeft: pl + 40 }}>{expandedContent}</ExpandedRegion>
      )}
    </>
  );
};

// Page chrome: header, current-selection bar, scrollable section area, footer
// actions, and the error alert. `children` is the section content; `overlays`
// renders alongside (e.g. a View Questions modal, which portals to body).
export const StandardPicker = ({
  groupCode,
  currentCard,
  isLoading = false,
  saving = false,
  canAssign = false,
  onAssign,
  onCancel,
  error = null,
  onClearError,
  children,
  overlays = null,
}) => {
  const currentMeta = currentCard?.track_type ? trackMeta(currentCard.track_type) : null;
  return (
    <PageContainer>
      <ContentHeader>
        <HeaderTitle>{groupCode ? `Group ${groupCode.toUpperCase()}: ` : ""}Edit Group Focus</HeaderTitle>
      </ContentHeader>

      {currentCard && (
        <CurrentBar>
          <CurrentLabel>Current Selection:</CurrentLabel>
          {currentMeta && (
            <Tag $bg={currentMeta.tagBg} $border={currentMeta.tagBorder} $color={currentMeta.tagText}>
              {currentMeta.label}
            </Tag>
          )}
          <CurrentCode>{currentCard.standard_code}:</CurrentCode>
          <CurrentName>{currentCard.standard_name}</CurrentName>
        </CurrentBar>
      )}

      {isLoading ? (
        <Centered>
          <FontAwesomeIcon icon={faSpinner} spin size="2x" />
        </Centered>
      ) : (
        <MainScroll>{children}</MainScroll>
      )}

      <Footer>
        <CancelButton onClick={onCancel} disabled={saving}>Cancel</CancelButton>
        <AssignButton onClick={onAssign} disabled={!canAssign || saving}>
          {saving ? <FontAwesomeIcon icon={faSpinner} spin /> : "Save"}
        </AssignButton>
      </Footer>

      {overlays}

      {error && (
        <Alert
          variant="danger"
          dismissible
          onClose={onClearError}
          style={{ position: "fixed", bottom: 88, right: 16, zIndex: 9999, maxWidth: 400 }}
        >
          {error}
        </Alert>
      )}
    </PageContainer>
  );
};
